import { Trans } from '@lingui/react/macro';

import type { User } from '@/utils/session/fetch-user';

import { usePaidCount } from '@/hooks/api/use-paid-count';
import { cx } from '@/utils/cx';

import type { AvatarProps } from '../base/avatar/avatar';

import { TotalEarnedDisplay } from '../total-earned-display';
import {
  AvatarProfileCard,
  AvatarProfileCardSkeleton,
} from './avatar-profile-card';

interface AvatarCardProps extends AvatarProps {
  user?: User | null;
}

export const AvatarCard = ({ className, user, ...props }: AvatarCardProps) => {
  const { data: paidCountData, isLoading: paidCountLoading } = usePaidCount(
    user?.id,
  );

  if (!user || paidCountLoading) {
    return <AvatarCardSkeleton className={className} />;
  }

  return (
    <div className="relative flex flex-col items-start gap-3 overflow-hidden rounded-xl bg-brand-primary p-6">
      <AvatarProfileCard
        user={user}
        className={cx('w-full', className)}
        {...props}
      />
      {paidCountData && (
        <>
          <p className={cx('text-secondary', 'text-md')}>
            <Trans>
              <strong>Total earned </strong>({paidCountData.paidCount} completed
              projects)
            </Trans>
          </p>
          <TotalEarnedDisplay amountInDollars={paidCountData.totalPaid} />
        </>
      )}
    </div>
  );
};

function AvatarCardSkeleton({ className }: { className?: string }) {
  return (
    <div className="relative flex animate-pulse flex-col items-start gap-3 rounded-xl bg-brand-primary p-4">
      <AvatarProfileCardSkeleton className={className} />
      {/* Paid count skeleton */}
      <div className="h-5 w-48 rounded bg-alpha-black-10" />
      <div className="h-6 w-32 rounded bg-alpha-black-10" />
    </div>
  );
}
